<div class="payroll-page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Staff Module</p>
      <h2>Payroll & Commissions</h2>
      <p class="muted">Configure delivery commissions and generate payroll slips for staff.</p>
    </div>
  </div>

  <div class="grid">
    <mat-card class="card" *ngIf="canManageCommissionConfig() || canManageDeliveryPricing()">
      <div class="card-header">
        <div>
          <p class="eyebrow">Commission</p>
          <h3>Per-Delivery Rates</h3>
        </div>
        <button mat-stroked-button color="primary" (click)="loadConfigs(); loadDeliveryPricing()" [disabled]="loadingConfigs">Refresh</button>
      </div>

      <form class="form" [formGroup]="configForm" (ngSubmit)="saveConfig()" *ngIf="canManageCommissionConfig()">
        <mat-form-field appearance="outline">
          <mat-label>Role</mat-label>
          <mat-select formControlName="roleKey">
            <mat-option value="rider">Rider</mat-option>
            <mat-option value="logistics_manager">Logistics Manager</mat-option>
            <mat-option value="tech">Tech</mat-option>
            <mat-option value="other">Other</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Commission Per Delivery (₦)</mat-label>
          <input matInput type="number" formControlName="commissionPerDelivery" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Effective From</mat-label>
          <input matInput type="date" formControlName="effectiveFrom" />
        </mat-form-field>

        <button mat-raised-button color="primary" type="submit" [disabled]="configForm.invalid || saving">
          Save Commission
        </button>
      </form>

      <div class="pricing" *ngIf="canManageDeliveryPricing()">
        <p class="eyebrow">Delivery Pricing</p>
        <form class="form pricing-form" [formGroup]="deliveryForm" (ngSubmit)="saveDeliveryPricing()">
          <mat-form-field appearance="outline">
            <mat-label>Amount Per Delivery (₦)</mat-label>
            <input matInput type="number" formControlName="amountPerDelivery" />
          </mat-form-field>
          <button mat-stroked-button color="primary" type="submit" [disabled]="deliveryForm.invalid || saving">Save Pricing</button>
        </form>
        <table class="table" *ngIf="pricingHistory.length">
          <thead>
            <tr>
              <th>Amount Per Delivery</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let p of pricingHistory">
              <td>₦{{ p.amountPerDelivery | number:'1.0-0' }}</td>
              <td>{{ p.updatedAt | date:'medium' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <mat-progress-bar *ngIf="loadingConfigs" mode="indeterminate"></mat-progress-bar>

      <table class="table" *ngIf="configs.length">
        <thead>
          <tr>
            <th>Role</th>
            <th>Rate (₦)</th>
            <th>Effective From</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let c of configs">
            <td>{{ rateLabel(c.roleKey) }}</td>
            <td>₦{{ c.commissionPerDelivery | number: '1.0-0' }}</td>
            <td>{{ c.effectiveFrom | date: 'mediumDate' }}</td>
          </tr>
        </tbody>
      </table>
    </mat-card>

    <mat-card class="card" *ngIf="canGeneratePayroll() || canViewSlips()">
      <div class="card-header">
        <div>
          <p class="eyebrow">Payroll</p>
          <h3>Generate Slip</h3>
        </div>
        <button mat-stroked-button color="primary" (click)="loadSlips()" [disabled]="loadingSlips">Refresh</button>
      </div>

      <form class="form" [formGroup]="slipForm" (ngSubmit)="generateSlip()" *ngIf="canGeneratePayroll()">
        <mat-form-field appearance="outline">
          <mat-label>Staff</mat-label>
          <mat-select formControlName="staffId">
            <mat-option *ngFor="let s of staff" [value]="s.id">{{ s.fullName }} ({{ s.position || '—' }})</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>From</mat-label>
          <input matInput type="date" formControlName="from" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>To</mat-label>
          <input matInput type="date" formControlName="to" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Deductions (₦)</mat-label>
          <input matInput type="number" formControlName="deductions" />
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Manual Units (optional)</mat-label>
          <input matInput type="number" formControlName="manualUnits" />
        </mat-form-field>

        <mat-slide-toggle formControlName="includeActivities">Include Activity Logs</mat-slide-toggle>

        <button mat-raised-button color="primary" type="submit" [disabled]="slipForm.invalid || saving">
          Generate Slip
        </button>
      </form>

      <div class="filter" [formGroup]="filterForm" *ngIf="canViewSlips()">
        <mat-form-field appearance="outline">
          <mat-label>Filter by Staff</mat-label>
          <mat-select formControlName="staffId" (selectionChange)="loadSlips()">
            <mat-option [value]="null">All</mat-option>
            <mat-option *ngFor="let s of staff" [value]="s.id">{{ s.fullName }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>From</mat-label>
          <input matInput type="date" formControlName="from" (change)="loadSlips()" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>To</mat-label>
          <input matInput type="date" formControlName="to" (change)="loadSlips()" />
        </mat-form-field>
      </div>

      <mat-progress-bar *ngIf="loadingSlips" mode="indeterminate"></mat-progress-bar>

      <table class="table" *ngIf="canViewSlips() && slips.length">
        <thead>
          <tr>
            <th>Staff</th>
            <th>Position</th>
            <th>Period</th>
            <th>Deliveries</th>
            <th>Rate</th>
            <th>Gross</th>
            <th>Deductions</th>
            <th>Net</th>
            <th>Generated</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let s of slips">
            <td>{{ s.staffName }}</td>
            <td>{{ s.position || '—' }}</td>
            <td>{{ s.periodFrom | date: 'MMM d' }} – {{ s.periodTo | date: 'MMM d, y' }}</td>
            <td>{{ s.deliveriesCount }}</td>
            <td>₦{{ s.commissionRate | number: '1.0-0' }}</td>
            <td>₦{{ s.grossAmount | number: '1.0-0' }}</td>
            <td>₦{{ s.deductions | number: '1.0-0' }}</td>
            <td class="strong">₦{{ s.netAmount | number: '1.0-0' }}</td>
            <td>{{ s.generatedAt | date: 'short' }}</td>
          </tr>
        </tbody>
      </table>

      <div class="empty" *ngIf="canViewSlips() && !loadingSlips && !slips.length">No payroll slips yet.</div>
    </mat-card>

  </div>
</div>
